/**
 * Régua de suporte ASSINADA −100…+100 do composto (E39.F1 / ADR-0054). // ux-neutrality:allow doc-comment
 *
 * Instrumento de BANCADA do perito: leitura PRINCIPAL é o índice assinado + o // ux-neutrality:allow doc-comment
 * nível verbal (forte/moderado/fraco/inconclusivo), ambos resolvidos no backend // ux-neutrality:allow doc-comment
 * (`composite_lr_support_index` / `composite_lr_support_level`). O LR cru fica // ux-neutrality:allow doc-comment
 * como leitura SECUNDÁRIA. Sinal = direção (apoio à mesma autoria `+` / à // ux-neutrality:allow doc-comment
 * heteroautoria `−`), módulo = força (teto LR=100 → ±100), centro = zona // ux-neutrality:allow doc-comment
 * inconclusiva. O motor de LR não muda — é camada de exibição/ajuste e NÃO sai // ux-neutrality:allow doc-comment
 * no laudo (E39.F2 / ADR-0053). O ajuste manual passa a aceitar o ÍNDICE, // ux-neutrality:allow doc-comment
 * convertido para LR (`10^(índice/50)`) antes de persistir (trilha do ADR-0044). // ux-neutrality:allow doc-comment
 */
import { useEffect, useState } from 'react';

import { Blueprint } from '@/components/ui/Blueprint';
import { CardKicker } from '@/components/ui/Card';
import { MonoData } from '@/components/ui/MonoData';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/Tooltip';
import { ptBR } from '@/i18n/pt-BR';
import type { LrAdjustmentStateData } from '@/lib/case_workspace';
import { fmtSupportIndex, lrToSupportIndex, SUPPORT_INDEX_MAX, SUPPORT_INDEX_MIN } from '@/lib/lr';
import { cn } from '@/lib/utils';
import { LrAdjustControl } from './LrAdjustControl';

export type LRDirection = 'h1' | 'h2' | 'inconclusive';
export type SupportLevel = 'inconclusivo' | 'fraco' | 'moderado' | 'forte';

/** Converte o índice assinado em posição 0–100% na régua (satura nas bordas). */
function indexToPercent(index: number): number {
  const clamped = Math.max(SUPPORT_INDEX_MIN, Math.min(SUPPORT_INDEX_MAX, index));
  return ((clamped - SUPPORT_INDEX_MIN) / (SUPPORT_INDEX_MAX - SUPPORT_INDEX_MIN)) * 100;
}

/**
 * Rótulo verbal do nível de bancada (E39.F1 / ADR-0054). DIREÇÃO e NÍVEL vêm // ux-neutrality:allow doc-comment
 * RESOLVIDOS do backend (precedência de banda + instabilidade já aplicada), // ux-neutrality:allow doc-comment
 * então a régua é só apresentação — concorda com o índice e com o laudo por // ux-neutrality:allow doc-comment
 * construção. Sem nível ou inconclusivo → rótulo inconclusivo seguro. // ux-neutrality:allow doc-comment
 */
const _LABEL_FOR_AUTHOR: Record<Exclude<SupportLevel, 'inconclusivo'>, string> = {
  forte: ptBR.workspaceCaso.lrClassificationStrongForAuthor,
  moderado: ptBR.workspaceCaso.lrClassificationModerateForAuthor,
  fraco: ptBR.workspaceCaso.supportLevelWeakForAuthor,
};
const _LABEL_AGAINST: Record<Exclude<SupportLevel, 'inconclusivo'>, string> = {
  forte: ptBR.workspaceCaso.lrClassificationStrongAgainst,
  moderado: ptBR.workspaceCaso.lrClassificationModerateAgainst,
  fraco: ptBR.workspaceCaso.supportLevelWeakAgainst,
};

function classify(direction: LRDirection | null, level: SupportLevel | null | undefined): string {
  // `!= null` (não `!== null`) também trata `undefined` — robusto se a resposta
  // omitir o campo. Sem nível, inconclusivo, ou direção inconclusiva/ausente →
  // rótulo inconclusivo (precedência de banda/instabilidade resolvida no backend).
  if (level == null || level === 'inconclusivo')
    return ptBR.workspaceCaso.lrClassificationInconclusive;
  if (direction === 'h1') return _LABEL_FOR_AUTHOR[level];
  if (direction === 'h2') return _LABEL_AGAINST[level];
  return ptBR.workspaceCaso.lrClassificationInconclusive;
}

interface TickConfig {
  index: number;
  label: string;
  /** Nome curto da zona sob a marca, como na prancha 2B. */
  zone: string;
  tooltip: string;
}

const TICKS: TickConfig[] = [
  {
    index: SUPPORT_INDEX_MIN,
    label: '−100',
    zone: ptBR.workspaceCaso.supportZoneAgainst,
    tooltip: ptBR.workspaceCaso.supportTickAgainst,
  },
  {
    index: 0,
    label: '0',
    zone: ptBR.workspaceCaso.supportZoneNeutral,
    tooltip: ptBR.workspaceCaso.supportTickNeutral,
  },
  {
    index: SUPPORT_INDEX_MAX,
    label: '+100',
    zone: ptBR.workspaceCaso.supportZoneForAuthor,
    tooltip: ptBR.workspaceCaso.supportTickForAuthor,
  },
];

/**
 * Cor da leitura principal: a do lado da régua em que o índice caiu. É cor de
 * SINAL — resultado e direção —, que é o único uso que o Design System permite.
 * A direção vem resolvida do backend; sem direção, a zona é a inconclusiva.
 */
function zona(direction: LRDirection | null): 'h1' | 'h2' | 'inconclusive' {
  return direction === 'h1' || direction === 'h2' ? direction : 'inconclusive';
}

const COR_DA_ZONA: Record<'h1' | 'h2' | 'inconclusive', string> = {
  h1: 'text-success-700',
  h2: 'text-danger-700',
  inconclusive: 'text-warning-700',
};

export interface LRMeterProps {
  /** LR composto efetivo (leitura SECUNDÁRIA + valor persistido no ajuste). */
  lr: number;
  /** Direção resolvida pelo backend (E16.F2.C01). `null` → inconclusivo seguro. */
  direction: LRDirection | null;
  /**
   * Índice de suporte assinado −100..+100 resolvido pelo backend (E39.F1/ADR-0054). // ux-neutrality:allow doc-comment
   * Leitura PRINCIPAL. `null`/`undefined` → derivado do LR como fallback. // ux-neutrality:allow doc-comment
   */
  supportIndex?: number | null;
  /** Nível verbal de bancada resolvido pelo backend (E39.F1/ADR-0054). */
  supportLevel?: SupportLevel | null;
  /**
   * E33.F2/ADR-0044 + E39.F1: ajuste manual pela mesa. Quando `onAdjust` e // ux-neutrality:allow doc-comment
   * `onClear` são passados, o índice vira input editável (converte para LR no // ux-neutrality:allow doc-comment
   * commit); sem eles, fica somente-leitura. // ux-neutrality:allow doc-comment
   */
  adjustment?: LrAdjustmentStateData | null;
  busy?: boolean;
  onAdjust?: (value: number) => void | Promise<void>;
  onClear?: () => void | Promise<void>;
}

export function LRMeter({
  lr,
  direction,
  supportIndex,
  supportLevel,
  adjustment = null,
  busy = false,
  onAdjust,
  onClear,
}: LRMeterProps): JSX.Element {
  // Índice exibido: o resolvido do backend (single-source); fallback ao espelho
  // determinístico do LR quando o campo não veio (respostas/fixtures legadas).
  const displayIndex = supportIndex ?? lrToSupportIndex(lr) ?? 0;
  const percent = indexToPercent(displayIndex);
  const editable = onAdjust !== undefined && onClear !== undefined;
  // E54.F5.T06: a agulha entra na posição NEUTRA e corre até o valor
  // (`--sc-slow`, 420ms, com o easing do sistema) — é a regra de movimento do
  // pacote para régua e gauge. Sob `prefers-reduced-motion` as durações do
  // sistema já vão a 0ms na origem, então o salto é instantâneo sem branch aqui.
  const [agulhaArmada, setAgulhaArmada] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setAgulhaArmada(true));
    return () => cancelAnimationFrame(id);
  }, []);
  const posicaoAgulha = agulhaArmada ? percent : indexToPercent(0);
  return (
    <Blueprint as="section" className="lr-meter" aria-label={ptBR.workspaceCaso.supportIndexTitle}>
      <header className="lr-meter-header">
        <div className="min-w-0">
          <CardKicker>{ptBR.workspaceCaso.supportIndexTitle}</CardKicker>
          <p>{ptBR.workspaceCaso.supportIndexSubtitle}</p>
        </div>
        {/* Leitura principal da bancada, à direita como na prancha 2B: o índice
            na cor da zona e o LR logo abaixo, como leitura secundária. */}
        <div
          className="lr-meter-value flex shrink-0 flex-col items-end"
          data-testid="lr-meter-value"
        >
          {editable ? (
            <LrAdjustControl
              effective={lr}
              kind="index"
              adjusted={adjustment?.composite_adjusted ?? false}
              busy={busy}
              testIdPrefix="composite"
              inputAriaLabel={ptBR.workspaceCaso.supportIndexAdjustLabel}
              onCommit={(value) => onAdjust(value)}
              onClear={() => onClear()}
            />
          ) : (
            <span
              className={cn('font-mono text-display-sm leading-none', COR_DA_ZONA[zona(direction)])}
              data-testid="lr-meter-index"
            >
              {fmtSupportIndex(displayIndex)}
            </span>
          )}
          {/* O nível verbal acompanha o número: a leitura da bancada é
              "índice + nível", e separá-los deixaria o valor sem o que ele
              significa. */}
          <span className="mt-1 text-heading-sm font-medium text-fg" data-testid="lr-meter-verbal">
            {classify(direction, supportLevel)}
          </span>
          <MonoData
            variant="label"
            className="lr-meter-secondary mt-1 normal-case"
            data-testid="lr-meter-secondary"
          >
            {ptBR.workspaceCaso.supportIndexSecondaryLr}: LR = {lr.toPrecision(3)}
          </MonoData>
        </div>
      </header>
      <div
        className="lr-meter-bar relative"
        role="img"
        aria-label={`${ptBR.workspaceCaso.supportIndexValueLabel} ${fmtSupportIndex(displayIndex)}`}
      >
        <div className="lr-meter-track" />
        <div
          className="lr-meter-marker transition-[left] duration-slow ease-sc"
          data-testid="lr-meter-marker"
          style={{ left: `${posicaoAgulha}%` }}
        />
        <div className="lr-meter-axis-zero" />
      </div>
      <div className="lr-meter-scale">
        {TICKS.map((tick) => (
          <Tooltip key={tick.index}>
            <TooltipTrigger asChild>
              <span
                data-testid={`lr-meter-tick-${tick.index}`}
                data-index-tick={tick.index}
                className="lr-meter-tick"
                style={{ left: `${indexToPercent(tick.index)}%` }}
                // Focável para que o Tooltip apareça via teclado (T07).
                // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                tabIndex={0}
                role="img"
                aria-label={`Marca ${tick.label}`}
              >
                <MonoData variant="label">{tick.label}</MonoData>
                <MonoData variant="label" className="lr-meter-tick-zone">
                  {tick.zone}
                </MonoData>
              </span>
            </TooltipTrigger>
            <TooltipContent>{tick.tooltip}</TooltipContent>
          </Tooltip>
        ))}
      </div>
    </Blueprint>
  );
}
